import React, { FunctionComponent, useReducer, useCallback, useEffect, useState, useRef } from 'react';

import classnames from 'classnames';
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { FeatureFlagId } from 'admin/feature-flags/FeatureFlags';
import { isAuthorized, useProfile } from 'auth/Auth';
import { FiltersProvider } from 'common/filters/Filters';
import { MessageBar } from 'common/message-bar/MessageBar';
import { MetaData } from 'common/response-editor/MetadataParser';
import { Overlay } from 'core/overlay/Overlay';
import { Switch } from 'core/switch/Switch';
import { AssetSelector } from 'social/publisher/asset-selector/AssetSelector';
import { DateTimePicker } from 'social/publisher/date-time-picker/DateTimePicker';
import { GoogleFieldsSelector } from 'social/publisher/google-fields-selector/GoogleFieldsSelector';
import { LocationPicker } from 'social/publisher/location-picker/LocationPicker';
import { MediaValidationWarning } from 'social/publisher/media-validation-warning/MediaValidationWarning';
import { PostSummary } from 'social/publisher/post-summary/PostSummary';
import { PostTypeSelector } from 'social/publisher/post-type-selector/PostTypeSelector';
import { PublishButton } from 'social/publisher/publish-button/PublishButton';
import { LocationUid } from 'social/publisher/Publisher.types';
import { PublisherContext, PublisherDispatchContext, initialViewState } from 'social/publisher/PublisherContext';
import {
    fetchAuthorizedSources,
    fetchSingleScheduledPost,
    fetchLibraryContent,
    fetchLocationUidsByCampaignId,
    fetchSingleCampaign,
    fetchLocations,
    // fetchNonApiAuthorizedSources,
} from 'social/publisher/PublisherFetchers';
import { publisherReducer } from 'social/publisher/PublisherReducers';
import { ResponseBox } from 'social/publisher/response-box/ResponseBox';
import { SourcePicker } from 'social/publisher/source-picker/SourcePicker';
import { SourcePreview } from 'social/publisher/source-preview/SourcePreview';
import { TitleEditor } from 'social/publisher/title-editor/TitleEditor';
import { YoutubeFieldsSelector } from 'social/publisher/youtube-fields-selector/YoutubeFieldsSelector';

import 'social/publisher/Publisher.scss';
import { LabelSelector } from './label-selector/LabelSelector';
import { OptimalTimesPicker } from './optimal-times-picker/OptimalTimesPicker';
import { SourceCustomizer } from './source-customizer/SourceCustomizer';

export const LOCAL_STORAGE_CONFIG_KEY = 'r4e-social-publisher-config';

export const Publisher: FunctionComponent = () => {
    const { t } = useTranslation(['social/publisher']);
    const [viewState, dispatch] = useReducer(publisherReducer, initialViewState);
    const [isReady, setIsReady] = useState(false); // Boolean to set whether all necessary items are loaded (for response editor)
    const [header, setHeader] = useState('');
    const [{ profile }] = useProfile();
    const [numSourcesSelected, setNumSourcesSelected] = useState<number>(0);
    const hasSmartSocialFeatureFlag = isAuthorized({ featureFlags: [FeatureFlagId.SocialSmartSocial] });

    const loadSources = useCallback(async () => {
        const search = {
            filters: [],
            tenantId: profile?.tenant?.id,
        };

        // TODO: Uncomment once implementing non-API sources
        // const nonApiSearch = {
        //     filters: [
        //         {
        //             name: '__s_source__',
        //             values: ['SNAPCHAT'],
        //         },
        //     ],
        //     tenantId: profile?.tenant?.id,
        // };

        try {
            const locationSourceResponse = await fetchAuthorizedSources(profile?.tenant?.id, search);
            // TODO: Uncomment once implementing non-API sources
            // const locationNonApiSourceResponse = await fetchNonApiAuthorizedSources(profile?.tenant?.id, nonApiSearch);
            // const nonApiLocationSources = locationNonApiSourceResponse?.locationSources?.filter(
            //     (locationSource) => 'SNAPCHAT' in locationSource.sources,
            // );
            const merged = _.merge(
                _.keyBy(locationSourceResponse.locationSources, 'lUid'),
                // _.keyBy(nonApiLocationSources, 'lUid'),
            );
            const locationSources = _.values(merged);

            dispatch({
                type: 'set-location-sources-data',
                payload: {
                    value: locationSources,
                },
            });

            if (locationSources.length === 1) {
                dispatch({
                    type: 'set-is-single-location-user',
                    payload: {
                        value: true,
                    },
                });
            }

            dispatch({
                type: 'set-profile-data',
                payload: {
                    value: profile,
                },
            });
        } catch {}
    }, [profile]);

    const loadLocalStorage = useCallback(async () => {
        const lsData = localStorage.getItem(LOCAL_STORAGE_CONFIG_KEY);
        let publisherConfig;

        // create or edit posts
        const setPostPublishMode = async (postId: string | undefined): Promise<void> => {
            setHeader('CREATE_POST');
            if (postId) {
                const data = await fetchSingleScheduledPost(profile?.tenant?.id, postId);

                if (data) {
                    dispatch({
                        type: 'set-scheduled-post-data',
                        payload: {
                            value: data,
                        },
                    });
                } else {
                    console.log('No matching post id found');
                }
            }
        };

        const setShareReviewMode = async (
            media: any,
            content: MetaData,
            isLocationsLocked: boolean,
            postType: string,
        ): Promise<void> => {
            setHeader('CREATE_POST');
            dispatch({
                type: 'set-share-review-data',
                payload: {
                    value: {
                        media,
                        content,
                        isLocationsLocked,
                        postType,
                    },
                },
            });
        };

        // publish custom/RSS content from library
        const setLibraryContentPublishMode = async (contentId: string): Promise<void> => {
            setHeader('PUBLISH_CONTENT_LIBRARY_ITEM');
            const data = await fetchLibraryContent(profile?.tenant?.id, contentId);

            if (data) {
                const locationUids = await fetchLocationUidsByCampaignId(profile?.tenant?.id, data.campaignId);

                dispatch({
                    type: 'set-library-content-publish-data',
                    payload: {
                        value: { libraryContent: data, libraryLocations: locationUids },
                    },
                });
            } else {
                console.log('no matching content id found');
            }
        };

        // publish campaign post from campaigns or library
        const setCampaignPostPublishMode = async (
            contentId: string,
            selectedLocations?: LocationUid[],
        ): Promise<void> => {
            setHeader('PUBLISH_CAMPAIGN_POST');
            const data = await fetchLibraryContent(profile?.tenant?.id, contentId);

            if (data) {
                let locationUids = await fetchLocationUidsByCampaignId(profile?.tenant?.id, data.campaignId);
                const campaign = await fetchSingleCampaign(profile?.tenant?.id, data.campaignId);

                if (selectedLocations) {
                    // make sure selected locations from publisherConfig is indeed part of the campaign locations
                    locationUids = selectedLocations.filter((el) => locationUids.indexOf(el) > -1);
                }
                dispatch({
                    type: 'set-campaign-post-publish-data',
                    payload: {
                        value: {
                            campaignContent: data,
                            campaignLocations: locationUids,
                            scheduleDateTimeRange: { minDate: campaign.startDate, maxDate: campaign.endDate },
                        },
                    },
                });
            } else {
                console.log('no matching content id found');
            }
        };

        // create or edit campaign post in campaigns
        const setCampaignPostCreateMode = async (campaignId: string, contentId?: string): Promise<void> => {
            setHeader('CREATE_CAMPAIGN_POST');
            const campaignData = await fetchSingleCampaign(profile?.tenant?.id, campaignId);
            let contentData;

            if (contentId) {
                contentData = await fetchLibraryContent(profile?.tenant?.id, contentId);
            }

            dispatch({
                type: 'set-campaign-post-create-data',
                payload: {
                    value: { campaignData, contentData },
                },
            });
        };

        // create or edit custom content in library
        const setLibraryContentCreateMode = async (contentId?: string): Promise<void> => {
            setHeader('CREATE_CONTENT');
            const myLocations = await fetchLocations(profile?.tenant?.agencyID!, profile?.tenant?.id);
            let contentdata;
            let locationUids;

            if (contentId) {
                contentdata = await fetchLibraryContent(profile?.tenant?.id, contentId);
                locationUids = await fetchLocationUidsByCampaignId(profile?.tenant?.id, contentdata.campaignId);
            }

            dispatch({
                type: 'set-library-content-create-data',
                payload: {
                    value: { libraryContent: contentdata, libraryLocations: locationUids, myLocations },
                },
            });
        };

        // Check localStorage
        if (typeof lsData === 'string') {
            try {
                publisherConfig = JSON.parse(lsData);
            } catch {}

            if (!(publisherConfig && typeof publisherConfig === 'object' && publisherConfig.editorMode)) {
                console.log('Invalid publisher config');
                return;
            }
            // apply settings in the config
            dispatch({
                type: 'set-publisher-config-data',
                payload: {
                    value: publisherConfig,
                },
            });

            switch (publisherConfig.editorMode) {
                case 'POST_PUBLISH':
                    await setPostPublishMode(publisherConfig.postId);
                    break;
                case 'CONTENT_PUBLISH':
                    await setLibraryContentPublishMode(publisherConfig.contentId);
                    break;
                case 'CAMPAIGN_PUBLISH':
                    await setCampaignPostPublishMode(publisherConfig.contentId, publisherConfig.selectedLocations);
                    break;
                case 'CAMPAIGN_CREATE':
                    await setCampaignPostCreateMode(publisherConfig.campaignId, publisherConfig.contentId);
                    break;
                case 'CONTENT_CREATE':
                    await setLibraryContentCreateMode(publisherConfig.contentId);
                    break;
                case 'SHARE_REVIEW':
                    await setShareReviewMode(
                        publisherConfig.media,
                        publisherConfig.content,
                        publisherConfig.isLocationsLocked,
                        publisherConfig.postType,
                    );
                    break;
                default:
                    await setPostPublishMode(publisherConfig.postId);
            }
        }
    }, [profile]);

    const clearLocalStorage = (): void => {
        localStorage.removeItem(LOCAL_STORAGE_CONFIG_KEY);
    };

    const handleClosePublisher = (): void => {
        clearLocalStorage();
        window.history.back();
    };

    const init = useCallback(async () => {
        dispatch({
            type: 'add-promises-in-transit',
            payload: {
                value: 'INIT',
            },
        });

        // Permissions Caching
        dispatch({
            type: 'set-has-social-permission',
            payload: {
                value: isAuthorized({ actions: [ActionId.SocialPublishPosts.Add, ActionId.SocialPublishPosts.Edit] }),
            },
        });

        dispatch({
            type: 'set-has-approve-auto-permission',
            payload: {
                value: isAuthorized({ actions: [ActionId.SocialPublishPosts.ApproveAuto] }),
            },
        });

        dispatch({
            type: 'set-has-profile-publish-permission',
            payload: {
                value: isAuthorized({ actions: [ActionId.DirectoryProfiles.Publish] }),
            },
        });

        dispatch({
            type: 'set-has-tag-add-permission',
            payload: {
                value: isAuthorized({ actions: [ActionId.SocialRepoTags.Add] }),
            },
        });

        dispatch({
            type: 'set-has-label-add-permission',
            payload: {
                value: isAuthorized({ actions: [ActionId.SocialRepoTags.Add] }),
            },
        });

        dispatch({
            type: 'set-has-label-view-permission',
            payload: {
                value: isAuthorized({ actions: [ActionId.SocialRepoTags.View] }),
            },
        });

        dispatch({
            type: 'set-has-content-add-global-permission',
            payload: {
                value: isAuthorized({ actions: [ActionId.SocialRepoContent.AddGlobal] }),
            },
        });

        dispatch({
            type: 'set-has-target-audience-permission',
            payload: {
                value: isAuthorized({ actions: [ActionId.SocialPublishPosts.TargetAudience] }),
            },
        });

        try {
            await trackPromise(loadSources(), 'social-publisher-editor');
            await trackPromise(loadLocalStorage(), 'social-publisher-editor');
        } catch {
        } finally {
            dispatch({
                type: 'remove-promises-in-transit',
                payload: {
                    value: 'INIT',
                },
            });

            setIsReady(true);
        }

        // TODO: check permissions with stuff like GMB-only, which will disable other sources
    }, [loadSources, loadLocalStorage]);

    useEffect((): void => {
        init();
    }, [init]);

    useEffect(() => {
        let numSourcesSelected = 0;

        Object.keys(viewState.selectedSources).forEach((key) => {
            if (viewState.selectedSources[key]) {
                numSourcesSelected += 1;
            }
        });

        setNumSourcesSelected(numSourcesSelected);
    }, [viewState.selectedSources]);

    // Used for communicating to ResponseEditor from another component
    const responseEditorRef = useRef<any>(null);
    const postTypeWrapper = classnames('r4e-social-publisher', {
        'post-type-media': viewState.postType === 'MEDIA',
        'post-type-link': viewState.postType === 'LINK',
    });

    return (
        <PublisherContext.Provider value={viewState}>
            <PublisherDispatchContext.Provider value={dispatch}>
                <FiltersProvider>
                    <div className={postTypeWrapper}>
                        <MessageBar />
                        <section className="r4e-social-publisher-header">
                            <Button
                                onClick={(): void => {
                                    console.log(viewState);
                                }}
                                className="hidden"
                            >
                                <span>Hidden Test</span>
                            </Button>
                            <h1>{t(header)}</h1>
                            <Button onClick={handleClosePublisher} className="close">
                                <span aria-hidden="true">×</span>
                                <span className="sr-only">{t('CLOSE')}</span>
                            </Button>
                        </section>
                        <section className="r4e-social-publisher-editor">
                            <Overlay area="social-publisher-editor" />
                            {isReady && (
                                <SourcePicker
                                    locationSources={viewState.locationSources}
                                    selectedSources={viewState.selectedSources}
                                    publisherErrors={viewState.publisherErrors}
                                    isSourcesLocked={!!viewState.isSourcesLocked}
                                    enabledSources={viewState.enabledSources}
                                    isLibraryContentCreateMode={viewState.editorMode === 'CONTENT_CREATE'}
                                    isCampaignCreateMode={
                                        viewState.editorMode === 'CAMPAIGN_CREATE' ||
                                        viewState.editorMode === 'CAMPAIGN_PUBLISH'
                                    }
                                />
                            )}
                            {isReady && !viewState.isLocationsDisabled && (
                                <LocationPicker
                                    locationSources={viewState.locationSources}
                                    selectedLocations={viewState.selectedLocations}
                                    selectedSources={viewState.selectedSources}
                                    isSingleLocationUser={viewState.isSingleLocationUser}
                                    publisherErrors={viewState.publisherErrors}
                                    isLocationsLocked={!!viewState.isLocationsLocked}
                                    enabledSources={viewState.enabledSources}
                                    isLibraryContentCreateMode={viewState.editorMode === 'CONTENT_CREATE'}
                                    selectedLocationGroup={viewState.selectedLocationGroup}
                                    enabledLocationGroups={viewState.enabledLocationGroups}
                                />
                            )}
                            {viewState.editorMode === 'POST_PUBLISH' &&
                                (numSourcesSelected > 1 || viewState.isCustomizingBySource) && (
                                    <section className="r4e-social-publisher-source-customizer">
                                        <SourceCustomizer
                                            selectedSources={viewState.selectedSources}
                                            customizeSourceKey={viewState.customizeSourceKey}
                                            isCustomizingBySource={viewState.isCustomizingBySource}
                                            responseEditorRef={responseEditorRef}
                                            sourceStates={viewState.sourceStates}
                                            publisherErrors={viewState.publisherErrors}
                                            promisesInTransit={viewState.promisesInTransit}
                                        />
                                    </section>
                                )}
                            {!viewState.isTitleDisabled && (
                                <TitleEditor
                                    title={viewState.title}
                                    publisherErrors={viewState.publisherErrors}
                                    isYoutube={viewState.selectedSources.YOUTUBE}
                                />
                            )}
                            <section className="r4e-social-publisher-content">
                                {!viewState.isAssetsDisabled && viewState.postType === 'MEDIA' && (
                                    <AssetSelector
                                        tenantId={profile?.tenant?.id}
                                        responseEditorRef={responseEditorRef}
                                        content={viewState.content}
                                        media={viewState.media}
                                        isAssetsLocked={!!viewState.isAssetsLocked}
                                    />
                                )}
                                {isReady && (
                                    <ResponseBox
                                        responseEditorRef={responseEditorRef}
                                        tenantId={profile?.tenant?.id}
                                        agencyId={profile?.tenant?.agencyID}
                                        selectedSources={viewState.selectedSources}
                                        content={viewState.content}
                                        media={viewState.media}
                                        mediaValidation={viewState.mediaValidation}
                                        publisherErrors={viewState.publisherErrors}
                                        isMentionEnabled={
                                            viewState.editorMode === 'POST_PUBLISH' ||
                                            viewState.editorMode === 'CAMPAIGN_CREATE' ||
                                            viewState.editorMode === 'CAMPAIGN_PUBLISH' ||
                                            viewState.editorMode === 'CONTENT_CREATE' ||
                                            viewState.editorMode === 'CONTENT_PUBLISH' ||
                                            viewState.editorMode === 'DRAFT'
                                        }
                                        isHashtagEnabled={
                                            viewState.editorMode === 'POST_PUBLISH' ||
                                            viewState.editorMode === 'CAMPAIGN_CREATE' ||
                                            viewState.editorMode === 'CAMPAIGN_PUBLISH' ||
                                            viewState.editorMode === 'CONTENT_CREATE' ||
                                            viewState.editorMode === 'CONTENT_PUBLISH' ||
                                            viewState.editorMode === 'DRAFT'
                                        }
                                        isPostTargetEnabled={
                                            (viewState.editorMode === 'POST_PUBLISH' ||
                                                viewState.editorMode === 'DRAFT') &&
                                            viewState.hasTargetAudiencePermission
                                        }
                                        targetAudience={viewState.targetAudience}
                                        isContentAndMediaLocked={!!viewState.isContentAndMediaLocked}
                                        isLocationTagEnabled={viewState.editorMode === 'POST_PUBLISH'}
                                        locationTag={viewState.locationTag}
                                        isDMLinkEnabled={
                                            viewState.editorMode === 'POST_PUBLISH' || viewState.editorMode === 'DRAFT'
                                        }
                                        customizeSourceKey={viewState.customizeSourceKey}
                                        mediaPathsToAltText={viewState.mediaPathsToAltText}
                                    />
                                )}
                            </section>
                            <MediaValidationWarning
                                responseEditorRef={responseEditorRef}
                                tenantId={profile?.tenant?.id}
                                selectedSources={viewState.selectedSources}
                                media={viewState.media}
                                mediaValidation={viewState.mediaValidation}
                                customizeSourceKey={viewState.customizeSourceKey}
                            />
                            <PostTypeSelector
                                content={viewState.content}
                                hasUserChosenMediaPostType={viewState.hasUserChosenMediaPostType}
                                postType={viewState.postType}
                                selectedSources={viewState.selectedSources}
                                tenantId={profile?.tenant?.id}
                                urlMetaData={viewState.urlMetaData}
                                publisherErrors={viewState.publisherErrors}
                                responseEditorRef={responseEditorRef}
                                customizeSourceKey={viewState.customizeSourceKey}
                                isShortenedUrl={viewState.isShortenedUrl}
                            />
                            {!viewState.isLabelsDisabled &&
                                viewState.hasSocialPermission &&
                                viewState.hasLabelViewPermission && (
                                    <LabelSelector
                                        selectedLabels={viewState.selectedLabels} // this can get updated via set-selected-labels in PublisherReducer
                                        selectedLabelIds={viewState.selectedLabelIds} // when editing posts, we only get the label ids from backend. LabelSelector will convert these to the label objects.
                                        tenantId={profile?.tenant?.id}
                                        publisherErrors={viewState.publisherErrors}
                                        hasLabelAddPermission={viewState.hasLabelAddPermission}
                                    />
                                )}
                            {viewState.selectedSources.GOOGLE_PLACES && (
                                <GoogleFieldsSelector
                                    googleFields={viewState.googleFields}
                                    publisherErrors={viewState.publisherErrors}
                                />
                            )}
                            {viewState.selectedSources.YOUTUBE && (
                                <YoutubeFieldsSelector
                                    youtubeFields={viewState.youtubeFields}
                                    publisherErrors={viewState.publisherErrors}
                                />
                            )}

                            {viewState.editorMode === 'CONTENT_CREATE' &&
                                hasSmartSocialFeatureFlag &&
                                (!viewState.selectedLocations ||
                                    (viewState.selectedLocations.length < 2 && (
                                        <div className="top30">
                                            <h5 className="bold">
                                                <i className="icon icon-label" />
                                                {t('ENABLE_SMART_SOCIAL')}
                                            </h5>
                                            <Switch
                                                id="enableSmartSocial"
                                                checked={viewState.isSmartReady}
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                                    dispatch({
                                                        type: 'set-is-smart-ready',
                                                        payload: { value: event.target.checked },
                                                    })
                                                }
                                            />
                                        </div>
                                    )))}
                        </section>

                        <section className="r4e-social-publisher-previewer">
                            <SourcePreview
                                selectedSources={viewState.selectedSources}
                                selectedLocations={viewState.selectedLocations}
                                locationSources={viewState.locationSources}
                                media={viewState.media}
                                content={viewState.content}
                                urlMetaData={viewState.urlMetaData}
                                googleFields={viewState.googleFields}
                                publisherErrors={viewState.publisherErrors}
                                title={viewState.title}
                                locationTag={viewState.locationTag}
                                targetAudience={viewState.targetAudience}
                                isDMLinkAdded={viewState.isDMLinkAdded}
                                customizeSourceKey={viewState.customizeSourceKey}
                                mediaPathsToAltText={viewState.mediaPathsToAltText}
                            />
                        </section>

                        <section className="r4e-social-publisher-footer">
                            {isReady && (
                                <DateTimePicker
                                    scheduleDateTime={viewState.scheduleDateTime}
                                    publishType={viewState.publishType}
                                    isSingleLocationUser={viewState.isSingleLocationUser}
                                    isPublishTimeLocked={!!viewState.isPublishTimeLocked}
                                    scheduleDateTimeRange={viewState.scheduleDateTimeRange}
                                    isMultiDateEnabled={
                                        (viewState.editorMode === 'POST_PUBLISH' || viewState.editorMode === 'DRAFT') &&
                                        !viewState.postId
                                    }
                                />
                            )}

                            {isReady && (
                                <OptimalTimesPicker
                                    tenantId={profile?.tenant?.id}
                                    publishType={viewState.publishType}
                                    selectedSources={viewState.selectedSources}
                                    sourceToPublishTime={viewState.sourceToPublishTime}
                                />
                            )}

                            <PublishButton
                                viewState={viewState}
                                onPublish={clearLocalStorage}
                                onDone={handleClosePublisher}
                            />
                        </section>
                    </div>
                    <div className="r4e-social-publisher-postsummary">
                        <PostSummary
                            isShowPostSummary={viewState.isShowPostSummary}
                            schedulePostResponse={viewState.schedulePostResponse}
                            onDone={handleClosePublisher}
                            isDuplicatePostEnabled={
                                viewState.editorMode === 'POST_PUBLISH' || viewState.editorMode === 'DRAFT'
                            }
                            selectedSources={viewState.selectedSources}
                        />
                    </div>
                </FiltersProvider>
            </PublisherDispatchContext.Provider>
        </PublisherContext.Provider>
    );
};
